import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/services/api";
import { toastService } from "@/services/toastService";
import type { VehicleIssue, CreateVehicleIssueRequest } from "@/types";

export interface UseVehicleIssuesOptions {
  vehicleId?: string;
  autoRefresh?: boolean;
}

export interface UseVehicleIssuesReturn {
  issues: VehicleIssue[];
  loading: boolean;
  error: string | null;
  total: number;
  createIssue: (
    data: CreateVehicleIssueRequest,
  ) => Promise<VehicleIssue | null>;
  updateIssue: (
    id: string,
    data: Partial<CreateVehicleIssueRequest>,
  ) => Promise<VehicleIssue | null>;
  deleteIssue: (id: string) => Promise<boolean>;
  refreshIssues: () => Promise<void>;
}

export function useVehicleIssues(
  options: UseVehicleIssuesOptions = {},
): UseVehicleIssuesReturn {
  const { vehicleId, autoRefresh = true } = options;
  const { t } = useTranslation();

  const [issues, setIssues] = useState<VehicleIssue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number>(0);

  const refreshIssues = useCallback(async (): Promise<void> => {
    if (!vehicleId) {
      setIssues([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.getVehicleIssuesByVehicle(vehicleId);
      setIssues(data);
      setTotal(data.length);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("pages:vehicles.issues.toast.fetchFailed");
      setError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, t]);

  const createIssue = async (
    data: CreateVehicleIssueRequest,
  ): Promise<VehicleIssue | null> => {
    setLoading(true);
    setError(null);

    try {
      const newIssue = await apiClient.createVehicleIssue(data);
      setIssues((prev) => [newIssue, ...prev]);
      setTotal((prev) => prev + 1);
      toastService.success(t("pages:vehicles.issues.toast.createSuccess"));
      return newIssue;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("pages:vehicles.issues.toast.createFailed");
      setError(errorMessage);
      toastService.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateIssue = async (
    id: string,
    data: Partial<CreateVehicleIssueRequest>,
  ): Promise<VehicleIssue | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedIssue = await apiClient.updateVehicleIssue(id, data);
      setIssues((prev) =>
        prev.map((issue) => (issue.id === id ? updatedIssue : issue)),
      );
      toastService.success(t("pages:vehicles.issues.toast.updateSuccess"));
      return updatedIssue;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("pages:vehicles.issues.toast.updateFailed");
      setError(errorMessage);
      toastService.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteIssue = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await apiClient.deleteVehicleIssue(id);
      setIssues((prev) => prev.filter((issue) => issue.id !== id));
      setTotal((prev) => prev - 1);
      toastService.success(t("pages:vehicles.issues.toast.deleteSuccess"));
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("pages:vehicles.issues.toast.deleteFailed");
      setError(errorMessage);
      toastService.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoRefresh && vehicleId) {
      refreshIssues();
    }
  }, [vehicleId, autoRefresh, refreshIssues]);

  return {
    issues,
    loading,
    error,
    total,
    createIssue,
    updateIssue,
    deleteIssue,
    refreshIssues,
  };
}
